import { Application } from '@features/applications';
import ApplicationViewModal from '@features/applications/components/ApplicationViewModal/ApplicationViewModal';
import {
  EventSignUp,
  EventsByMonth,
  InterviewBlock,
  UserEvent,
} from '@features/enterpriseEvents';
import InterviewBlockModal from '@features/enterpriseEvents/components/AddInterviewBlockModal/InterviewBlockModal';
import DeleteCalendarBlockModal from '@features/enterpriseEvents/components/DeleteCalendarBlockModal/DeleteCalendarBlockModal';
import DeleteInterviewModal from '@features/enterpriseEvents/components/DeleteInterviewModal/DeleteInterviewModal';
import UpdateInterviewInvitationModal from '@features/enterpriseEvents/components/UpdateInterviewInvitationModal/UpdateInterviewInvitationModal';
import { useGetBlockData } from '@features/enterpriseEvents/hooks/useGetBlockData';
import { useGetInterviewBlockSignUps } from '@features/enterpriseEvents/hooks/useGetInterviewBlockSignUps';
import { getTimeZoneAbbreviation } from '@features/profile';
import { AddCircleOutline } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import {
  Alert,
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { authContext } from '@staffedupusa/shared-components';
import { darkBlue, lightGrey, primaryBlue, white } from '@styles/colors';
import dayjs from 'dayjs';
import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';

import { OpenInterview } from '../../../../vectors/index';

type EventInfoModalProps = {
  event: InterviewBlock | UserEvent | null;
  blockId: number | string | null;
  handleClose: () => void;
  setEvents: Dispatch<SetStateAction<EventsByMonth | []>>;
  setSelectedApplication: Dispatch<SetStateAction<Application | null>>;
  selectedApplication: Application | null;
  handlePageNo?: (newPageNo: number) => void;
};

const EventInfoModal: FC<EventInfoModalProps> = ({
  setEvents,
  handlePageNo,
  blockId,
  event,
  handleClose,
  setSelectedApplication,
  selectedApplication,
}) => {
  const { authData } = useContext(authContext);
  const { blockData, isBlockLoading } = useGetBlockData({
    selectedId: blockId,
    setEvents,
  });
  const browserTimeZone = getTimeZoneAbbreviation();
  const [
    isUpdateInterviewInvitationModalOpen,
    setIsUpdateInterviewInvitationModalOpen,
  ] = useState(false);
  const [isUpdateCalendarBlockModalOpen, setIsUpdateCalendarBlockModalOpen] =
    useState(false);
  const [isDeleteCalendarBlockModalOpen, setIsDeleteCalendarBlockModalOpen] =
    useState(false);
  const [isDeleteInterviewModalOpen, setIsDeleteInterviewModalOpen] =
    useState(false);

  const isOpenInterviews = Boolean(blockData?.object as InterviewBlock);
  const { interviewSignupsData } = useGetInterviewBlockSignUps({
    blockId: isOpenInterviews
      ? (blockData?.object as InterviewBlock).id
      : undefined,
  });

  const updateRating = (id: number, newRating: number) => {
    if (selectedApplication && selectedApplication.id === id) {
      setSelectedApplication((prevApp) =>
        prevApp ? { ...prevApp, rating: newRating } : null,
      );
    }
  };

  const renderDateTime = ({
    startTime,
    endTime,
  }: {
    startTime: number;
    endTime: number;
  }) => (
    <>
      {startTime && (
        <Typography
          component="span"
          variant="body1"
          sx={{
            display: 'flex',
            alignItems: 'center',
            maxWidth: '198px',
            width: '100%',
            alignSelf: 'center',
          }}
        >
          <CalendarMonthIcon fontSize="large" sx={{ mr: 2 }} />
          {dayjs(startTime).format('MMM DD, YYYY')}
        </Typography>
      )}
      <Typography
        component="span"
        variant="body1"
        sx={{
          display: 'flex',
          alignItems: 'center',
          maxWidth: '198px',
          width: '100%',
          alignSelf: 'center',
        }}
      >
        <AccessTimeIcon fontSize="large" sx={{ mr: 2 }} />
        {`${dayjs(startTime).format('hh:mm')} - ${dayjs(endTime).format('hh:mm a')} ${browserTimeZone || ''}`}
      </Typography>
    </>
  );

  const renderScheduledBy = (event: any) => (
    <Typography
      component="span"
      variant="body1"
      sx={{
        display: 'flex',
        alignItems: 'center',
        margin: '20px 0 !important',
        width: '100%',
      }}
    >
      <AddCircleOutline fontSize="large" sx={{ mr: 2 }} />
      <strong style={{ marginRight: '10px' }}>Scheduled by:</strong>{' '}
      {typeof event.is_enterprise === 'string'
        ? event.is_enterprise
        : event.company.s_name ?? authData?.user?.enterprise?.name}
    </Typography>
  );

  const renderCommonUi = ({
    startTime,
    endTime,
    event,
  }: {
    startTime: number;
    endTime: number;
    event: object;
  }) => (
    <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={1}>
      {renderDateTime({ startTime, endTime })}
      {renderScheduledBy(event)}
    </Stack>
  );

  const renderApplicantInterviewLayout = ({
    interview,
    application,
  }: UserEvent) => {
    return (
      <Stack spacing={2} sx={{ marginBottom: 3 }}>
        <Stack spacing={1} alignItems="center">
          <Avatar
            src={(application.applicant.photo as string) || ''}
            sx={{ width: 50, height: 50 }}
          />
          <Typography variant="h3">{application.applicant.name}</Typography>
          <Typography variant="subtitle1">{application.position}</Typography>
          <Button
            onClick={() => setSelectedApplication(application)}
            variant="outlined"
          >
            View Application Details
          </Button>
        </Stack>
        {renderCommonUi({
          startTime: interview.dt_start_time,
          endTime: interview.dt_end_time,
          event,
        })}
      </Stack>
    );
  };

  const renderOpenInterviewsLayout = (interviewBlock: InterviewBlock) => {
    const companyInfo = interviewBlock?.jobs?.[0]?.job_listing?.company;
    const physical_address_concate =
      companyInfo?.additional_info?.physical_interview_location ||
      `${companyInfo?.s_address1 || companyInfo?.s_address2 || ''} ${companyInfo?.s_city || ''} ${companyInfo?.s_state || ''} ${companyInfo?.s_zip || ''}`.trim();

    const mapToDayOfWeek = (item: string) => {
      switch (item) {
        case '6':
          return 'Sun';
        case '0':
          return 'Mon';
        case '1':
          return 'Tue';
        case '2':
          return 'Wed';
        case '3':
          return 'Thu';
        case '4':
          return 'Fri';
        case '5':
          return 'Sat';
        default:
          return 'Invalid day';
      }
    };

    return (
      <Stack spacing={3} sx={{ marginBottom: 3 }}>
        {interviewBlock.available_spots > 0 && (
          <Alert severity="info">{`${interviewBlock.available_spots} Spots Available`}</Alert>
        )}
        {Boolean(
          interviewSignupsData && interviewSignupsData?.object?.length,
        ) && (
          <List>
            {interviewSignupsData?.object?.map((signUp: EventSignUp) => (
              <ListItem sx={{ p: 0 }} key={signUp.id}>
                <ListItemButton
                  onClick={() => setSelectedApplication(signUp.application)}
                >
                  <Stack
                    spacing={5}
                    justifyContent="space-between"
                    alignItems="center"
                    direction="row"
                  >
                    <Stack spacing={3} alignItems="center" direction="row">
                      <Avatar
                        src={
                          (signUp.application.applicant.photo as string) || ''
                        }
                        sx={{ width: 50, height: 50 }}
                      />
                      <Stack spacing={0}>
                        <Typography variant="subtitle1">
                          {signUp.application.applicant.name}
                        </Typography>
                        <Typography variant="body1">
                          {signUp.application.position}
                        </Typography>
                      </Stack>
                    </Stack>
                    {signUp?.slot && (
                      <Typography
                        component="span"
                        variant="body1"
                        sx={{
                          display: 'inline-block',
                          fontSize: '0.8rem',
                          backgroundColor: lightGrey,
                          color: darkBlue,
                          padding: '0.5rem 1rem',
                          marginBottom: '5px !important',
                          borderRadius: 5,
                        }}
                      >
                        {`${dayjs(signUp?.slot?.slot_start_time).format('hh:mm')} - ${dayjs(signUp?.slot?.slot_end_time).format('hh:mm a')} ${browserTimeZone || ''}`}
                      </Typography>
                    )}
                  </Stack>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        <Typography variant="h4" gutterBottom>
          Block Details
        </Typography>

        <Box sx={{ pl: '1px' }}>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              pl: '5px',
              width: '100%',
            }}
            textAlign="center"
            alignSelf="center"
          >
            <strong>Interview type :</strong>
            <span style={{ marginLeft: '4px' }}>
              {interviewBlock?.block_type === 1 ? 'In Person' : 'Virtual'}
            </span>
          </Typography>

          {interviewBlock?.block_type === 1 ? (
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '4px',
                width: '100%',
              }}
              textAlign="center"
              alignSelf="center"
            >
              <LocationOnOutlinedIcon
                fontSize="medium"
                sx={{ mr: 1, color: 'black' }}
              />

              <span style={{ paddingLeft: '10px' }}>
                {physical_address_concate}
              </span>
            </Typography>
          ) : (
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: '4px',
                width: '100%',
                marginTop: '4px',
              }}
              textAlign="center"
              alignSelf="center"
            >
              <VideocamOutlinedIcon
                sx={{ mr: 2, color: 'black' }}
                fontSize="medium"
              />

              <a
                style={{ paddingLeft: '5px', textDecoration: 'none' }}
                target="_blank"
                href={companyInfo?.additional_info?.virtual_interview_link}
                rel="noreferrer"
              >
                Join meeting
              </a>
            </Typography>
          )}
        </Box>

        {renderCommonUi({
          startTime: interviewBlock.dt_start_time,
          endTime: interviewBlock.dt_end_time,
          event,
        })}

        {interviewBlock.jobs && interviewBlock.jobs.length > 0 && (
          <>
            <Typography variant="h5" gutterBottom>
              Jobs
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flexWrap: 'wrap' }}
            >
              {interviewBlock.jobs.map((job) => {
                return (
                  <Typography
                    component="span"
                    variant="body1"
                    sx={{
                      display: 'inline-block',
                      fontSize: '0.8rem',
                      backgroundColor: lightGrey,
                      color: darkBlue,
                      padding: '0.5rem 1rem',
                      marginBottom: '5px !important',
                      borderRadius: 5,
                    }}
                  >
                    {job.job_listing.s_title}
                  </Typography>
                );
              })}
            </Stack>
          </>
        )}
        {interviewBlock.slots &&
          interviewBlock.slots?.filter((item) => item?.status !== 0)?.length >
            0 && (
            <>
              <Typography variant="h5" gutterBottom>
                Available Slots
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ flexWrap: 'wrap' }}
              >
                {interviewBlock.slots
                  .filter((item) => item?.status !== 0)
                  .map((job) => {
                    return (
                      <Typography
                        component="span"
                        variant="body1"
                        sx={{
                          display: 'inline-block',
                          fontSize: '0.8rem',
                          backgroundColor: lightGrey,
                          color: darkBlue,
                          padding: '0.5rem 1rem',
                          marginBottom: '5px !important',
                          borderRadius: 5,
                        }}
                      >
                        {`${dayjs(job.slot_start_time).format('hh:mm')} - ${dayjs(job.slot_end_time).format('hh:mm a')}`}
                      </Typography>
                    );
                  })}
              </Stack>
            </>
          )}
        {interviewBlock.repeat?.repeat && (
          <>
            <Typography>Repeat on:</Typography>
            <Stack
              direction="row"
              alignItems="center"
              sx={{ flexWrap: 'wrap', gap: '5px' }}
            >
              {interviewBlock.repeat?.days.split(',').map((item) => {
                return (
                  <Typography
                    component="span"
                    variant="body1"
                    sx={{
                      display: 'inline-block',
                      fontSize: '0.8rem',
                      backgroundColor: primaryBlue,
                      color: white,
                      padding: '0.3rem 1rem',
                      borderRadius: 5,
                    }}
                  >
                    {mapToDayOfWeek(item)}
                  </Typography>
                );
              })}
            </Stack>
            <Typography>
              {interviewBlock.repeat.ends_type === 0 ? (
                <Typography>
                  Will <b>never end</b>
                </Typography>
              ) : interviewBlock.repeat.ends_type === 1 &&
                interviewBlock.repeat.ends_on ? (
                <Typography>
                  Will end on <b>{interviewBlock.repeat.ends_on}</b>
                </Typography>
              ) : (
                interviewBlock.repeat.ends_type === 2 &&
                interviewBlock.repeat.ends_on && (
                  <Typography>
                    Will end after <b>{interviewBlock.repeat.ends_on}</b>{' '}
                    Occurrences
                  </Typography>
                )
              )}
            </Typography>
          </>
        )}
        <Stack
          spacing={2}
          direction={{ xs: 'column', sm: 'row' }}
          sx={{ width: '100%' }}
          alignItems="center"
          justifyContent="center"
        >
          {interviewBlock.master_block === null && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              fullWidth
              onClick={
                isOpenInterviews
                  ? () => setIsUpdateCalendarBlockModalOpen(true)
                  : () => setIsUpdateInterviewInvitationModalOpen(true)
              }
            >
              Edit
            </Button>
          )}

          <Button
            variant="contained"
            color="error"
            startIcon={<ClearIcon />}
            fullWidth
            onClick={
              isOpenInterviews
                ? () => setIsDeleteCalendarBlockModalOpen(true)
                : () => setIsDeleteInterviewModalOpen(true)
            }
          >
            Delete
          </Button>
        </Stack>
      </Stack>
    );
  };

  if (!blockData?.object) {
    return null;
  }

  return (
    <>
      <Dialog open onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <br />
          <OpenInterview />
          <br />
          <Typography variant="h4" sx={{ p: 3 }}>
            {isOpenInterviews ? (
              <>
                {isOpenInterviews
                  ? Array.isArray(blockData?.object?.slots) &&
                    blockData.object.slots.length > 0
                    ? 'Interview Blocks'
                    : !(
                          Array.isArray(blockData?.object?.slots) &&
                          blockData.object.slots.length
                        )
                      ? 'Open Interviews'
                      : 'Interview'
                  : 'Interview'}{' '}
                for <b>{blockData?.object?.company?.s_name}</b>
              </>
            ) : (
              'Interview'
            )}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {isOpenInterviews
            ? renderOpenInterviewsLayout(blockData?.object as InterviewBlock)
            : renderApplicantInterviewLayout(blockData?.object as UserEvent)}
        </DialogContent>
      </Dialog>
      {isOpenInterviews ? (
        <DeleteCalendarBlockModal
          isOpen={isDeleteCalendarBlockModalOpen}
          handleClose={() => setIsDeleteCalendarBlockModalOpen(false)}
          onSuccess={handleClose}
          handlePageNo={handlePageNo}
          calendarBlock={event as InterviewBlock}
        />
      ) : (
        <DeleteInterviewModal
          isOpen={isDeleteInterviewModalOpen}
          handleClose={() => setIsDeleteInterviewModalOpen(false)}
          onSuccess={handleClose}
          handlePageNo={handlePageNo}
          interview={(event as UserEvent).interview}
        />
      )}
      {isOpenInterviews ? (
        <InterviewBlockModal
          isOpen={isUpdateCalendarBlockModalOpen}
          handleClose={() => setIsUpdateCalendarBlockModalOpen(false)}
          interviewBlock={blockData?.object as InterviewBlock}
          onSuccess={handleClose}
        />
      ) : (
        <UpdateInterviewInvitationModal
          isOpen={isUpdateInterviewInvitationModalOpen}
          handleClose={() => setIsUpdateInterviewInvitationModalOpen(false)}
          onSuccess={handleClose}
          userEvent={event as UserEvent}
        />
      )}
      {selectedApplication && (
        <ApplicationViewModal
          handleClose={() => setSelectedApplication(null)}
          application={selectedApplication}
          searchValue=""
          setApplication={setSelectedApplication}
          updateRating={updateRating}
        />
      )}
    </>
  );
};

export default EventInfoModal;
